import React from "react";
import { FileText, Buildings, Cube, Upload, Trash, Toolbox, Package, UsersThree } from "@phosphor-icons/react";
import { ResourceType } from "../../../shared/types/resource";
import { ResourceChip, ResourceData } from "../../../shared/components/UniversalPickerModal/components/items/ResourceItem";
import { useObjectUrls } from "shared/hooks/useObjectUrls";
import { FileThumbnail } from "shared/components/upload/FileThumbnail";
import { getFileKey } from "shared/utils/fileKey";

// Rental contract detection logic (same as FileUploadComponent)
const RENTAL_LABELS = [
  "Huurcontract",
  "Bijlage aan huurcontract",
  "Addendum aan huurcontract",
  "Huurhernieuwingsaanvraag",
  "Huurwaarborg",
  "Ingaande plaatsbeschrijving",
  "Uitgaande plaatsbeschrijving",
  "Identiteitskaart huurder",
  "Betalingsbewijs huur",
  "Indexatiebrief",
  "Brandverzekering huurder",
  "Communicatie huurder",
  "Registratiebewijs huurcontract",
  "Opzegbrief",
  "Registratiebewijs plaatsbeschrijving",
  "Reglement inwendige orde",
  "Communicatie - Vredegerecht",
  "Vonnis - Vredegerecht",
  "Verzoeksschrift - Vredegerecht",
  "Dagvaarding - Vredegerecht",
  "Huurgeschil - stukkenbundel intern",
  "Huurgeschil - stukkenbundel huurder",
  "Communicatie huurgeschil - Eigen advocaat",
  "Communicatie huurgeschil - Advocaat huurder"
];

const isRentalRelatedDocument = (label: string): boolean => {
  return RENTAL_LABELS.includes(label);
};

// Utility function to determine the selection condition type for a file
type SelectionCondition = 'no-selection' | 'building-only' | 'building-and-unit';

const getFileSelectionCondition = (fileLogItem: any): SelectionCondition => {
  const hasBuildingId = fileLogItem.fileLog.buildingId;
  const hasUnitId = fileLogItem.fileLog.unitId;

  if (hasBuildingId && hasUnitId) {
    return 'building-and-unit';
  } else if (hasBuildingId && !hasUnitId) {
    return 'building-only';
  } else {
    return 'no-selection';
  }
};

// Check if selected files have similar conditions for bulk label actions
const validateBulkLabelSelection = (fileLogs: any[], selectedIndexes: Set<number>): {
  isValid: boolean;
  conditions: SelectionCondition[];
  message?: string;
} => {
  const selectedFiles = Array.from(selectedIndexes).map(index => fileLogs[index]);
  const conditions = selectedFiles.map(getFileSelectionCondition);
  const uniqueConditions = [...new Set(conditions)];

  console.log('Validation debug:', {
    selectedFiles: selectedFiles.map(f => ({
      buildingId: f.fileLog.buildingId,
      unitId: f.fileLog.unitId
    })),
    conditions,
    uniqueConditions
  });

  if (uniqueConditions.length === 1) {
    return { isValid: true, conditions: uniqueConditions };
  }

  const conditionNames = {
    'no-selection': 'geen gebouw/unit geselecteerd',
    'building-only': 'alleen gebouw geselecteerd',
    'building-and-unit': 'gebouw en unit geselecteerd'
  };

  const conditionsList = uniqueConditions.map(c => conditionNames[c]).join(', ');

  return {
    isValid: false,
    conditions: uniqueConditions,
    message: `U kunt geen labels instellen voor bestanden met verschillende condities. Geselecteerde bestanden hebben: ${conditionsList}. Selecteer alleen bestanden met dezelfde condities voor bulk label acties.`
  };
};

interface DocumentsTableProps {
  fileLogs: any[];
  files: File[];
  selectedFileIndexes: Set<number>;
  uploadingFileIndexes?: Set<number>;
  removingFileIndexes?: Set<number>;
  resources: any[];
  renovationProjects?: any[];
  projectParts?: any[];
  onToggleFileSelection: (index: number, selected: boolean) => void;
  onSelectAll: (selected: boolean) => void;
  onUploadFiles: (indexes: number[]) => void;
  onRemoveFile: (index: number) => void;
  /** Drops every selected row from the queue. */
  onRemoveSelected?: () => void;
  onInputChange: (e: any, index: number, field: string) => void;
  onPdfClick?: (url: string, name: string) => void;
  onResetAllRows?: () => void;
  onShowValidationWarning?: (message: string) => void;
  isLoading: boolean;
}

const DocumentsTable: React.FC<DocumentsTableProps> = ({
  fileLogs,
  files,
  selectedFileIndexes,
  uploadingFileIndexes = new Set(),
  removingFileIndexes = new Set(),
  resources,
  renovationProjects = [],
  projectParts = [],
  onToggleFileSelection,
  onSelectAll,
  onUploadFiles,
  onRemoveFile,
  onRemoveSelected,
  onPdfClick,
  onResetAllRows,
  onShowValidationWarning,
  isLoading,
}) => {
  // Only used to open a PDF in the viewer, which needs the actual file. Image
  // previews go through FileThumbnail instead of a URL to the full-size original.
  const getObjectUrl = useObjectUrls(files);

  const handlePickerEvent = (mode: string, index: number) => {
    const isBulkActive = selectedFileIndexes.size > 1; // Need multiple files for bulk

    // Validate bulk label selection if multiple files are selected and it's a label mode
    if (isBulkActive && (mode === 'file-label' || mode === 'label')) {
      const validation = validateBulkLabelSelection(fileLogs, selectedFileIndexes);
      console.log('Validation result:', validation, 'Selected indexes:', Array.from(selectedFileIndexes));
      if (!validation.isValid && onShowValidationWarning) {
        onShowValidationWarning(validation.message || 'Selectie niet geldig voor bulk label actie');
        return; // Don't proceed with picker if validation fails
      }
    }

    const event = new CustomEvent('file-upload-open-picker', {
      detail: { mode, fileIndex: index, bulkSelection: isBulkActive }
    });
    window.dispatchEvent(event);
  };

  // Calculate checkbox states
  const allSelected = selectedFileIndexes.size === files.length && files.length > 0;
  const someSelected = selectedFileIndexes.size > 0 && selectedFileIndexes.size < files.length;


  return (
    <div className="relative">
      {/* Desktop Bulk Upload Controls */}
      {selectedFileIndexes.size > 0 && (
        <div className="mb-4 p-4 bg-moss-primary/5 border border-moss-primary/20 rounded-lg hidden sm:block">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-moss-primary">
                {selectedFileIndexes.size} bestanden geselecteerd
              </span>
              <button
                onClick={() => onSelectAll(false)}
                className="text-xs text-neutral-secondary-mid hover:text-neutral-primary"
              >
                Deselecteer alles
              </button>
            </div>
            <div className="flex items-center gap-2">
              {/* Removing a wrong selection should be no harder than uploading it. */}
              {onRemoveSelected && (
                <button
                  onClick={onRemoveSelected}
                  disabled={isLoading}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg border border-neutral-primary/20 text-neutral-primary hover:bg-neutral-primary/5 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Trash size={16} />
                  Verwijder selectie
                </button>
              )}
              <button
                onClick={() => onUploadFiles(Array.from(selectedFileIndexes))}
                disabled={isLoading}
                className="flex items-center gap-2 px-4 py-2 bg-moss-primary text-white rounded-lg hover:bg-moss-primaryDark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Upload size={16} />
                Upload geselecteerde bestanden
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Top Bar */}
      <div className="sm:hidden sticky top-0 z-40 bg-white border-b border-gray-200 px-4 py-3 mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h3 className="text-base font-semibold text-neutral-primary">
              {fileLogs.length} foto{fileLogs.length !== 1 ? "'s" : ""}
            </h3>
            {selectedFileIndexes.size > 0 && (
              <div className="flex items-center gap-2 px-3 py-1 bg-moss-primary/10 rounded-full">
                <div className="w-2 h-2 bg-moss-primary rounded-full"></div>
                <span className="text-sm font-medium text-moss-primary">
                  {selectedFileIndexes.size}
                </span>
              </div>
            )}
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              const allChecked = selectedFileIndexes.size === fileLogs.length;
              onSelectAll(!allChecked);
            }}
            className="text-sm font-medium text-moss-primary active:text-moss-primaryDark transition-colors touch-manipulation px-3 py-2"
          >
            {selectedFileIndexes.size === fileLogs.length ? 'Deselect' : 'Select All'}
          </button>
        </div>
      </div>

      {/* Mobile Floating Upload Button */}
      {selectedFileIndexes.size > 0 && (
        <div className="sm:hidden fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50">
          <button
            onClick={() => onUploadFiles(Array.from(selectedFileIndexes))}
            disabled={isLoading}
            className="flex items-center gap-2 px-6 py-3 bg-moss-primary text-white rounded-full shadow-2xl hover:bg-moss-primaryDark disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 touch-manipulation border-2 border-white"
            style={{ minWidth: '140px' }}
          >
            <Upload size={18} className="shrink-0" />
            <span className="font-semibold">
              Upload {selectedFileIndexes.size}
            </span>
          </button>
        </div>
      )}

      {/* Mobile Image Grid View */}
      <div className="sm:hidden pb-24 px-4">
        {fileLogs.length > 0 && (
          <div className="grid grid-cols-2 gap-3">
            {fileLogs.map((fileLogItem, index) => {
              const isUploading = uploadingFileIndexes.has(index);
              const isRemoving = removingFileIndexes.has(index);
              const isSelected = selectedFileIndexes.has(index);
              const isImage = fileLogItem.file.type.startsWith('image/');

              return (
                <div
                  key={getFileKey(fileLogItem.file)}
                  className={`relative rounded-lg border-2 transition-all duration-200 ${
                    isSelected
                      ? 'border-moss-primary bg-moss-primary/5 shadow-md'
                      : 'border-gray-200 bg-white hover:border-moss-primary/40'
                  } ${isUploading ? 'bg-moss-primary/10 border-moss-primary/40' : ''}`}
                  onClick={() => onToggleFileSelection(index, !isSelected)}
                >
                  {/* Selection Overlay */}
                  <div
                    className="absolute inset-0 rounded-lg bg-moss-primary/20 opacity-0 transition-opacity duration-200 pointer-events-none"
                    style={{ opacity: isSelected ? 0.1 : 0 }}
                  />

                  {/* Selection Checkbox */}
                  <div className="absolute top-2 left-2 z-10">
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                      isSelected
                        ? 'bg-moss-primary border-moss-primary'
                        : 'bg-white border-gray-300'
                    }`}>
                      {isSelected && (
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  </div>

                  {/* Status Badge */}
                  {(isUploading || isRemoving) && (
                    <div className="absolute top-2 right-2 z-10">
                      <div className="bg-brick-primary text-white text-xs px-2 py-1 rounded-full">
                        {isUploading ? 'Uploaden...' : 'Verwijderen...'}
                      </div>
                    </div>
                  )}

                  {/* Image/File Preview */}
                  <div className="aspect-square rounded-t-lg overflow-hidden bg-gray-100">
                    {isImage ? (
                      <FileThumbnail
                        file={fileLogItem.file}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <FileText size={32} className="text-gray-400" />
                      </div>
                    )}
                  </div>

                  {/* File Info */}
                  <div className="p-3">
                    <div className="text-xs font-medium text-neutral-primary truncate mb-1">
                      {fileLogItem.file.name}
                    </div>
                    <div className="text-xs text-gray-500 mb-2">
                      {(fileLogItem.file.size / 1024 / 1024).toFixed(1)} MB
                    </div>

                    {/* Quick Status Indicators */}
                    <div className="flex flex-wrap gap-1">
                      {fileLogItem.fileLog.buildingId && (
                        <span className="inline-flex items-center gap-1 text-xs bg-moss-primary/10 text-moss-primary px-2 py-0.5 rounded-full">
                          <Buildings size={10} />
                          ✓
                        </span>
                      )}
                      {fileLogItem.fileLog.unitId && (
                        <span className="inline-flex items-center gap-1 text-xs bg-brick-primary/10 text-brick-primary px-2 py-0.5 rounded-full">
                          <Cube size={10} />
                          ✓
                        </span>
                      )}
                      {fileLogItem.fileLog.renovationProjectId && (
                        <span className="inline-flex items-center gap-1 text-xs bg-blue-500/10 text-blue-700 px-2 py-0.5 rounded-full">
                          <Toolbox size={10} />
                          ✓
                        </span>
                      )}
                      {fileLogItem.fileLog.projectPartId && (
                        <span className="inline-flex items-center gap-1 text-xs bg-purple-500/10 text-purple-700 px-2 py-0.5 rounded-full">
                          <Package size={10} />
                          ✓
                        </span>
                      )}
                      {fileLogItem.fileLog.fileLabel && (
                        <span className="text-xs bg-neutral-100 text-neutral-600 px-2 py-0.5 rounded-full">
                          Label ✓
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Desktop Table View */}
      <div className="hidden sm:block overflow-x-auto">
        <table className="w-full border-collapse">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200">
            <th className="text-left p-3 w-12">
              <input
                type="checkbox"
                className="w-5 h-5 sm:w-4 sm:h-4 rounded-sm border border-neutral-primary/30 cursor-pointer touch-manipulation"
                style={{ accentColor: '#5B7304' }}
                checked={allSelected}
                ref={(el) => {
                  if (el) el.indeterminate = someSelected;
                }}
                onChange={(e) => onSelectAll(e.target.checked)}
              />
            </th>
            <th className="text-left p-3 text-sm font-medium text-neutral-primary">
              Bestand
            </th>
            <th className="text-left p-3 text-sm font-medium text-neutral-primary">
              <span className="flex items-center gap-1">
                Gebouw
                <span className="text-red-500 text-sm">*</span>
              </span>
            </th>
            <th className="text-left p-3 text-sm font-medium text-neutral-primary">
              <span className="flex items-center gap-1">
                Unit
                <span className="text-red-500 text-sm">*</span>
              </span>
            </th>
            <th className="text-left p-3 text-sm font-medium text-neutral-primary">
              Renovatieproject
            </th>
            <th className="text-left p-3 text-sm font-medium text-neutral-primary">
              Projectonderdeel
            </th>
            <th className="text-left p-3 text-sm font-medium text-neutral-primary">
              <span className="flex items-center gap-1">
                Label
                <span className="text-red-500 text-sm">*</span>
              </span>
            </th>
            <th className="text-left p-3 text-sm font-medium text-neutral-primary">
              Relaties
            </th>
            <th className="text-left p-3 text-sm font-medium text-neutral-primary">
              Status
            </th>
            <th className="text-left p-3 text-sm font-medium text-neutral-primary">
              Documentdatum
            </th>
            <th className="text-left p-3 text-sm font-medium text-neutral-primary">
              Asset Link
            </th>
            <th className="text-left p-3 text-sm font-medium text-neutral-primary">
              <div className="flex items-center gap-2">
                <span>Acties</span>
                {onResetAllRows && (
                  <button
                    onClick={onResetAllRows}
                    className="px-2 py-1 text-xs bg-neutral-primary/10 hover:bg-neutral-primary/20 text-neutral-primary rounded-sm transition-colors"
                    title="Reset alle rijen naar standaard"
                  >
                    Reset
                  </button>
                )}
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {fileLogs.map((fileLogItem, index) => {
            const isUploading = uploadingFileIndexes.has(index);
            const isRemoving = removingFileIndexes.has(index);
            const isSelected = selectedFileIndexes.has(index);

            return (
              <tr
                key={getFileKey(fileLogItem.file)}
                className={`border-b border-gray-100 transition-all duration-300 ease-in-out ${
                  isRemoving ? 'opacity-0 scale-95 pointer-events-none' : 'opacity-100 scale-100'
                } ${
                  isUploading ? 'bg-moss-primary/10' : ''
                } ${
                  isSelected ? 'bg-moss-primary/5' : 'hover:bg-gray-50'
                } ${
                  fileLogItem.file.type === 'application/pdf' ? 'cursor-pointer' : ''
                }`}
                onClick={() => {
                  if (fileLogItem.file.type === 'application/pdf' && onPdfClick) {
                    onPdfClick(getObjectUrl(fileLogItem.file), fileLogItem.file.name);
                  }
                }}
              >
                <td className="p-3">
                  <input
                    type="checkbox"
                    className="w-5 h-5 sm:w-4 sm:h-4 rounded-sm border border-neutral-primary/30 cursor-pointer touch-manipulation"
                    style={{ accentColor: '#5B7304' }}
                    checked={isSelected}
                    onChange={(e) => {
                      e.stopPropagation();
                      onToggleFileSelection(index, e.target.checked);
                    }}
                  />
                </td>
                <td className="p-3">
                <div className="flex items-center gap-2">
                  {fileLogItem.file.type === 'application/pdf' ? (
                    <FileText size={16} className="text-red-600" />
                  ) : fileLogItem.file.type.startsWith('image/') ? (
                    <div className="w-6 h-6 rounded-sm overflow-hidden border border-gray-200">
                      <FileThumbnail
                        file={fileLogItem.file}
                        className="w-full h-full object-cover"
                        maxDimension={64}
                      />
                    </div>
                  ) : (
                    <FileText size={16} className="text-gray-400" />
                  )}
                  <span className="truncate max-w-32">{fileLogItem.file.name}</span>
                </div>
              </td>
              <td className="p-3">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePickerEvent('building', index);
                  }}
                  className="w-full text-left px-2 py-2 sm:px-1.5 sm:py-1 h-full rounded-sm border text-xs sm:text-[11px] hover:bg-neutral-primary/5 border-neutral-primary/20 bg-white text-neutral-primary transition-all duration-200 ease-in-out hover:shadow-sm hover:border-moss-primary/40 focus:ring-2 focus:ring-moss-primary/20 focus:outline-hidden min-w-0 touch-manipulation min-h-[36px] sm:min-h-auto"
                >
                  <div className="flex items-center gap-1 min-w-0">
                    <Buildings className="h-3 w-3 text-neutral-secondary-mid shrink-0" />
                    <span className="truncate">
                      {(() => {
                        const buildingId = fileLogs[index]?.fileLog?.buildingId;
                        if (buildingId) {
                          const building = resources.find(r => r.id === buildingId && r.type === ResourceType.BUILDING);
                          return building?.resourceId || buildingId;
                        }
                        return 'Selecteer gebouw';
                      })()}
                    </span>
                  </div>
                </button>
              </td>
              <td className="p-3">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePickerEvent('unit', index);
                  }}
                  className="w-full text-left px-2 py-2 sm:px-1.5 sm:py-1 h-full rounded-sm border text-xs sm:text-[11px] hover:bg-neutral-primary/5 border-neutral-primary/20 bg-white text-neutral-primary transition-all duration-200 ease-in-out hover:shadow-sm hover:border-moss-primary/40 focus:ring-2 focus:ring-moss-primary/20 focus:outline-hidden min-w-0 touch-manipulation min-h-[36px] sm:min-h-auto"
                >
                  <div className="flex items-center gap-1 min-w-0">
                    <Cube className="h-3 w-3 text-neutral-secondary-mid shrink-0" />
                    <span className="truncate">
                      {(() => {
                        const unitId = fileLogs[index]?.fileLog?.unitId;
                        if (unitId) {
                          const unit = resources.find(r => r.id === unitId && r.type === ResourceType.UNIT);
                          return unit?.resourceId || unitId;
                        }
                        return 'Selecteer unit';
                      })()}
                    </span>
                  </div>
                </button>
              </td>
              <td className="p-3">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePickerEvent('renovation-project', index);
                  }}
                  className="w-full text-left px-2 py-2 sm:px-1.5 sm:py-1 h-full rounded-sm border text-xs sm:text-[11px] hover:bg-neutral-primary/5 border-neutral-primary/20 bg-white text-neutral-primary transition-all duration-200 ease-in-out hover:shadow-sm hover:border-moss-primary/40 focus:ring-2 focus:ring-moss-primary/20 focus:outline-hidden min-w-0 touch-manipulation min-h-[36px] sm:min-h-auto"
                >
                  <div className="flex items-center gap-1 min-w-0">
                    <Toolbox className="h-3 w-3 text-neutral-secondary-mid shrink-0" />
                    <span className="truncate">
                      {(() => {
                        const renovationProjectId = fileLogs[index]?.fileLog?.renovationProjectId;
                        if (renovationProjectId) {
                          const project = renovationProjects.find(p => p.id === renovationProjectId);
                          return project?.title || renovationProjectId;
                        }
                        return 'Selecteer project';
                      })()}
                    </span>
                  </div>
                </button>
              </td>
              <td className="p-3">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePickerEvent('project-part', index);
                  }}
                  className="w-full text-left px-2 py-2 sm:px-1.5 sm:py-1 h-full rounded-sm border text-xs sm:text-[11px] hover:bg-neutral-primary/5 border-neutral-primary/20 bg-white text-neutral-primary transition-all duration-200 ease-in-out hover:shadow-sm hover:border-moss-primary/40 focus:ring-2 focus:ring-moss-primary/20 focus:outline-hidden min-w-0 touch-manipulation min-h-[36px] sm:min-h-auto"
                >
                  <div className="flex items-center gap-1 min-w-0">
                    <Package className="h-3 w-3 text-neutral-secondary-mid shrink-0" />
                    <span className="truncate">
                      {(() => {
                        const projectPartId = fileLogs[index]?.fileLog?.projectPartId;
                        if (projectPartId) {
                          const part = projectParts.find(p => p.id === projectPartId);
                          return part?.title || projectPartId;
                        }
                        return 'Selecteer onderdeel';
                      })()}
                    </span>
                  </div>
                </button>
              </td>
              <td className="p-3">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePickerEvent('label', index);
                  }}
                  className="text-left p-2 hover:bg-gray-100 rounded-sm text-sm min-w-24"
                >
                  {fileLogItem.fileLog.fileLabel?.label || 'Selecteer label'}
                </button>
              </td>
              <td className="p-3">
                {/* Relations Column */}
                {(() => {
                  const relations = fileLogItem.fileLog.relations || [];
                  const hasRelations = relations.length > 0;

                  if (hasRelations) {
                    return (
                      <div className="flex flex-col gap-1">
                        <div className="flex flex-wrap gap-1 max-w-[200px]">
                          {relations.slice(0, 3).map((relation: ResourceData, idx: number) => (
                            <ResourceChip
                              key={`${relation.id}-${idx}`}
                              resource={relation}
                              onRemove={() => {
                                // Dispatch event to remove relation
                                const event = new CustomEvent('file-upload-remove-relation', {
                                  detail: { fileIndex: index, relationId: relation.id }
                                });
                                window.dispatchEvent(event);
                              }}
                            />
                          ))}
                          {relations.length > 3 && (
                            <span className="text-xs text-neutral-secondary-mid">
                              +{relations.length - 3} meer
                            </span>
                          )}
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePickerEvent('resource', index);
                          }}
                          className="text-xs text-moss-primary hover:text-moss-primaryDark"
                        >
                          Voeg toe
                        </button>
                      </div>
                    );
                  }

                  return (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePickerEvent('resource', index);
                      }}
                      className="px-3 py-1 border border-moss-primary/30 text-moss-primary rounded text-xs hover:bg-moss-primary/5 transition-colors flex items-center gap-1"
                    >
                      <UsersThree size={12} />
                      + Relatie
                    </button>
                  );
                })()}
              </td>
              <td className="p-3">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
handlePickerEvent('status', index);
                  }}
                  className="text-left p-2 hover:bg-gray-100 rounded-sm text-sm min-w-20"
                >
                  {fileLogItem.fileLog.status || 'Selecteer status'}
                </button>
              </td>
              <td className="p-3">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePickerEvent('documentDate', index);
                  }}
                  className="text-left p-2 hover:bg-gray-100 rounded text-sm min-w-32"
                >
                  {fileLogItem.fileLog.documentDate
                    ? new Date(fileLogItem.fileLog.documentDate).toLocaleDateString('nl-NL', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      })
                    : 'Selecteer datum'}
                </button>
              </td>
              <td className="p-3">
                {(() => {
                  const fileLabel = fileLogItem.fileLog.fileLabel?.label;
                  const isRentalDoc = fileLabel && isRentalRelatedDocument(fileLabel);
                  const customData = fileLogItem.fileLog.customData;
                  const hasRentalContract = customData?.rentalContractId;

                  if (isRentalDoc) {
                    if (hasRentalContract) {
                      return (
                        <div className="flex items-center gap-2">
                          <div className="text-xs">
                            <div className="font-medium text-moss-primary">
                              {customData.rentalContractName || 'Huurcontract'}
                            </div>
                            <div className="text-neutral-secondary-mid">
                              Contract ID: {customData.rentalContractId}
                            </div>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                    handlePickerEvent('rental-contract', index);
                            }}
                            className="text-xs text-brick-primary hover:text-brick-primaryDark"
                          >
                            Wijzigen
                          </button>
                        </div>
                      );
                    } else {
                      return (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                handlePickerEvent('rental-contract', index);
                          }}
                          className="px-3 py-1 border border-brick-primary/30 text-brick-primary rounded-sm text-xs hover:bg-brick-secondary-fine transition-colors"
                        >
                          + Huurcontract
                        </button>
                      );
                    }
                  }

                  // Show asset link button for non-rental documents
                  const hasAsset = customData?.assetId;

                  if (hasAsset) {
                    return (
                      <div className="flex items-center gap-2">
                        <div className="text-xs">
                          <div className="font-medium text-moss-primary">
                            {customData.assetName || 'Asset'}
                          </div>
                          <div className="text-neutral-secondaryMid">
                            Asset ID: {customData.assetId}
                          </div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePickerEvent('asset', index);
                          }}
                          className="text-xs text-brick-primary hover:text-brick-primaryDark"
                        >
                          Wijzigen
                        </button>
                      </div>
                    );
                  }

                  return (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePickerEvent('asset', index);
                      }}
                      className="px-3 py-1 border border-moss-primary/30 text-moss-primary rounded text-xs hover:bg-moss-primary/5 transition-colors"
                    >
                      + Asset
                    </button>
                  );
                })()}
              </td>
              <td className="p-3">
                <div className="flex gap-1">
                  <button
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent PDF viewer from opening
                      if (!isUploading) onUploadFiles([index]);
                    }}
                    className={`px-2 py-1 rounded text-xs transition-colors flex items-center gap-1 ${
                      isUploading
                        ? 'bg-moss-primary/70 text-white cursor-not-allowed'
                        : isLoading
                        ? 'bg-gray-400 text-gray-300 cursor-not-allowed'
                        : 'bg-moss-primary text-white hover:bg-moss-primary/80'
                    }`}
                    disabled={isLoading || isUploading}
                    title={isUploading ? "Uploading..." : "Upload dit bestand"}
                  >
                    {isUploading ? (
                      <>
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                        <span>Uploading...</span>
                      </>
                    ) : (
                      <Upload size={12} />
                    )}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent PDF viewer from opening
                      onRemoveFile(index);
                    }}
                    className="px-2 py-1 bg-red-500 text-white rounded-sm text-xs hover:bg-red-600 transition-colors"
                    title="Verwijder bestand"
                  >
                    <Trash size={12} />
                  </button>
                </div>
              </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
    </div>
  );
};

export default DocumentsTable;